// src/pages/LoginPage.tsx
// Login page with Central Identity and legacy auth support
//
// AUTH_MODE behavior:
// - 'central': Immediately redirects to Central's hosted OIDC login page.
// - 'hybrid':  Shows "Continue with Central" button + legacy form tab.
// - 'legacy':  Shows legacy email/password form only.

import { useState, useEffect, FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch } from '../store/store';
import { setCredentials } from '../store/slices/authSlice';
import { useLoginMutation, useDevLoginMutation } from '../api/authApi';
import {
  showCentralAuth,
  showLegacyAuth,
  AUTH_MODE,
  initiateCentralLogin,
} from '../config/centralAuth';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Card from '../components/ui/Card';

interface LocationState {
  from?: { pathname: string };
}

type AuthTab = 'central' | 'legacy';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  // Get the page user was trying to access
  const from = (location.state as LocationState)?.from?.pathname || '/';

  // Check if user just logged out — suppress auto-redirect to prevent re-authentication.
  // The session is already destroyed server-side via POST /api/oidc/session/logout,
  // but we keep this flag as a safety net in case the session destroy was slow.
  const searchParams = new URLSearchParams(location.search);
  const justLoggedOut = searchParams.get('logged_out') === '1';

  // Central-only mode: redirect immediately to Central OIDC (unless just logged out)
  const shouldRedirectToCentral =
    AUTH_MODE === 'central' && showCentralAuth() && !justLoggedOut;

  const [redirecting, setRedirecting] = useState(shouldRedirectToCentral);

  useEffect(() => {
    if (shouldRedirectToCentral) {
      initiateCentralLogin(from).catch((err) => {
        console.error('[LoginPage] Central redirect failed:', err);
        setRedirecting(false);
        setError('Failed to redirect to Central Identity. Please try again.');
      });
    }
  }, [shouldRedirectToCentral, from]);

  // Determine default tab for hybrid/legacy modes
  const defaultTab: AuthTab = showCentralAuth() ? 'central' : 'legacy';
  const [activeTab, setActiveTab] = useState<AuthTab>(defaultTab);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Legacy auth mutations
  const [login, { isLoading: isLoginLoading }] = useLoginMutation();
  const [devLogin, { isLoading: isDevLoginLoading }] = useDevLoginMutation();

  const isLoading = isLoginLoading || isDevLoginLoading || redirecting;

  // Handle Central login redirect (hybrid mode button click)
  const handleCentralRedirect = async () => {
    setRedirecting(true);
    setError(null);
    try {
      await initiateCentralLogin(from);
    } catch {
      setError('Failed to initiate Central login. Please try again.');
      setRedirecting(false);
    }
  };

  // Handle legacy login
  const handleLegacyLogin = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email) {
      setError('Email is required');
      return;
    }

    try {
      const result = await login({ email, password }).unwrap();
      dispatch(setCredentials({ token: result.token, user: result.user }));
      navigate(from, { replace: true });
    } catch (err: unknown) {
      const apiError = err as { data?: { error?: string } };
      setError(apiError.data?.error || 'Login failed. Please try again.');
    }
  };

  // Handle dev login (legacy only)
  const handleDevLogin = async () => {
    setError(null);

    try {
      const result = await devLogin({ email: email || undefined }).unwrap();
      dispatch(setCredentials({ token: result.token, user: result.user }));
      navigate(from, { replace: true });
    } catch (err: unknown) {
      const apiError = err as { data?: { error?: string } };
      setError(apiError.data?.error || 'Dev login failed. Please try again.');
    }
  };

  // Central-only mode: show a loading screen while redirecting
  if (shouldRedirectToCentral) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">LevelUp</h1>
          <p className="text-gray-500">Redirecting to Central Identity...</p>
        </div>
      </div>
    );
  }

  // Show tab selector only in hybrid mode
  const showTabs = AUTH_MODE === 'hybrid' && showCentralAuth() && showLegacyAuth();

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">LevelUp</h1>
          <p className="mt-2 text-gray-600">Sign in to your account</p>
        </div>

        <Card className="p-6">
          {/* Auth Mode Tabs (hybrid mode only) */}
          {showTabs && (
            <div className="flex mb-6 border-b border-gray-200">
              <button
                type="button"
                onClick={() => setActiveTab('central')}
                className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'central'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Central Identity
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('legacy')}
                className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'legacy'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                LevelUp Account
              </button>
            </div>
          )}

          {/* Central Tab — "Continue with Central" button */}
          {activeTab === 'central' && showCentralAuth() && (
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg text-center">
                <p className="text-sm text-blue-700">
                  Sign in with your LevelUp Central account
                </p>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <Button
                type="button"
                variant="primary"
                size="lg"
                onClick={handleCentralRedirect}
                isLoading={redirecting}
                disabled={isLoading}
                className="w-full"
              >
                Continue with Central Identity
              </Button>
            </div>
          )}

          {/* Legacy Tab — email/password form */}
          {(activeTab === 'legacy' || !showCentralAuth()) && (
            <form onSubmit={handleLegacyLogin} className="space-y-4">
              {/* Error Message */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  autoFocus
                />
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                isLoading={isLoginLoading}
                disabled={isLoading}
                className="w-full"
              >
                Sign In
              </Button>
            </form>
          )}

          {/* Dev Login Section (legacy tab only, dev mode only) */}
          {(activeTab === 'legacy' || !showCentralAuth()) && import.meta.env.DEV && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500 text-center mb-4">
                Development Mode
              </p>
              <Button
                type="button"
                variant="secondary"
                size="md"
                onClick={handleDevLogin}
                isLoading={isDevLoginLoading}
                disabled={isLoading}
                className="w-full"
              >
                Login as Demo User
              </Button>
              <p className="mt-2 text-xs text-gray-400 text-center">
                Creates a demo user if one doesn't exist
              </p>
            </div>
          )}
        </Card>

        {/* Footer */}
        <p className="mt-6 text-center text-sm text-gray-500">
          Don't have an account?{' '}
          {activeTab === 'central' && showCentralAuth() ? (
            <button
              type="button"
              onClick={handleCentralRedirect}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Sign up with Central
            </button>
          ) : (
            <a href="#" className="text-blue-600 hover:text-blue-700 font-medium">
              Contact us
            </a>
          )}
        </p>

        {/* Auth Mode Info (dev only) */}
        {import.meta.env.DEV && (
          <p className="mt-4 text-center text-xs text-gray-400">
            Auth Mode: {AUTH_MODE} | Tab: {activeTab}
          </p>
        )}
      </div>
    </div>
  );
}
